import * as React from "react"

import { Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import type { FinanceOverviewQuery } from "@/features/finance/model/finance-types"
import { useWidgetMenu } from "@ibada/dashboard-engine"
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display"
import { BranchType } from "@ibada/shared-types/common"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { formatMoneySafe } from "@/features/finance/overview/lib/overview-format"

interface BranchRow {
  id: string
  name: string
  income: string
  expense: string
  net: string
  /** Negative net is the thing a treasurer is scanning for, so it is styled rather than merely printed. */
  netIsNegative: boolean
}

interface BranchPerformanceData {
  currency: string
  rows: BranchRow[]
}

/**
 * Branch performance — income, expense and net for every branch, side by side.
 *
 * The one view that only exists at GLOBAL scope. A branch or parish overview is already scoped to a single
 * branch, so this table would be one row restating the KPI cards above it; consolidated, it answers the
 * question the other contexts structurally cannot ask — who is growing, who is stalling, and who has stopped
 * posting altogether.
 *
 * Rows come from the backend already sorted by net descending and already totalled per branch. Nothing is
 * summed here: ADR-0011 puts authoritative aggregates on the server, and a client that re-adds branch rows is
 * exactly the drift that decision exists to prevent.
 */
export function BranchPerformanceWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()
  const { useFinanceOverviewQuery } = useFinanceFeatureQuery()

  const query = useFinanceOverviewQuery(
    scope.workspaceId && period.from && period.to
      ? ({
          workspaceId: scope.workspaceId,
          context: scope.overviewContext,
          dateFrom: period.from,
          dateTo: period.to,
          // NOTE: no `profile` — the dashboard profile trims breakdowns, and this table IS a breakdown.
        } satisfies FinanceOverviewQuery)
      : null,
  )

  const data = React.useMemo<BranchPerformanceData | undefined>(() => {
    if (!query.data) return undefined
    const response = query.data
    const table = response.tables?.find((entry) => entry.id === "branchPerformance")
    const currency = response.meta?.currency ?? scope.currency

    const rows = (table?.rows ?? []).map((row) => {
      const record = row as Record<string, unknown>
      const name = typeof record.name === "string" ? record.name : ""
      const net = typeof record.balance === "string" ? record.balance : "0"
      return {
        id: typeof record.id === "string" ? record.id : name,
        // The system branch is provisioned as `SYSTEM_BRANCH_<workspace>`; to a treasurer it is the Parish.
        name: getBranchDisplayLabel({
          branchType: name.startsWith("SYSTEM_BRANCH_") ? BranchType.System : BranchType.Normal,
          branchName: name,
          parishLabel: t("widgets.branchPerformance.parish"),
          fallbackLabel: name,
        }),
        income: typeof record.totalIncome === "string" ? record.totalIncome : "0",
        expense: typeof record.totalExpense === "string" ? record.totalExpense : "0",
        net,
        netIsNegative: net.trim().startsWith("-"),
      }
    })

    return { currency, rows }
  }, [query.data, scope.currency, t])

  useWidgetMenu({ refresh: () => void query.refetch() })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={data}
      isEmpty={(d) => d.rows.length === 0}
      skeleton={<BranchSkeleton />}
      errorTitle={t("widgets.branchPerformance.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.branchPerformance.empty.title")}
    >
      {(d) => (
        /*
         * TWO COLUMNS BY DEFAULT, four when the widget is actually wide enough for them.
         *
         * Four money columns do not fit a one-span tile: the amounts ran into each other and into the branch
         * name, which is worse than useless on a table whose whole job is comparing figures. Sizing off a
         * CONTAINER query rather than the viewport is what makes that correct — the widget can be resized and
         * expanded independently of the window, so a `md:` breakpoint would have described the wrong box.
         *
         * Nothing is lost at the narrow size: income and expense move under the branch name as a signed pair,
         * where +/− carries the meaning that the column headers carried.
         */
        <div className='@container min-w-0'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='text-muted-foreground text-xs'>
                <th className='py-1 text-left font-medium'>{t("widgets.branchPerformance.columns.branch")}</th>
                <th className='hidden py-1 pl-3 text-right font-medium @md:table-cell'>
                  {t("widgets.branchPerformance.columns.income")}
                </th>
                <th className='hidden py-1 pl-3 text-right font-medium @md:table-cell'>
                  {t("widgets.branchPerformance.columns.expense")}
                </th>
                <th className='py-1 pl-3 text-right font-medium'>{t("widgets.branchPerformance.columns.net")}</th>
              </tr>
            </thead>
            <tbody className='divide-border/60 divide-y'>
              {d.rows.map((row) => (
                <tr key={row.id}>
                  <td className='min-w-0 py-1.5 pr-2'>
                    <span className='block truncate'>{row.name}</span>
                    <span className='text-muted-foreground block truncate text-xs tabular-nums @md:hidden'>
                      +{formatMoneySafe(row.income, d.currency)} · −{formatMoneySafe(row.expense, d.currency)}
                    </span>
                  </td>
                  <td className='hidden py-1.5 pl-3 text-right tabular-nums @md:table-cell'>
                    {formatMoneySafe(row.income, d.currency)}
                  </td>
                  <td className='hidden py-1.5 pl-3 text-right tabular-nums @md:table-cell'>
                    {formatMoneySafe(row.expense, d.currency)}
                  </td>
                  <td
                    className={
                      row.netIsNegative
                        ? "text-destructive py-1.5 pl-3 text-right font-medium whitespace-nowrap tabular-nums"
                        : "py-1.5 pl-3 text-right font-medium whitespace-nowrap tabular-nums"
                    }
                  >
                    {formatMoneySafe(row.net, d.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </WidgetBoundary>
  )
}

function BranchSkeleton() {
  return (
    <div className='space-y-2'>
      {[0, 1, 2, 3].map((row) => (
        <Skeleton key={row} className='h-6 w-full' />
      ))}
    </div>
  )
}
